import type {
  BudgetTrackingControllerGetCostsData,
  DataCompletenessControllerGetDataCompletenessData,
  MeterControllerGetEnergyEvaluationData,
  MeterControllerGetMetersData,
  MeterControllerGetMetersInfoByAllowedLocationsData,
} from '@/src/api/types/generated-types'
import type { GranularityEnum } from '@/src/enums/granularity.enum'
import type { ProcurementEnergyType } from '@/src/features/procurement-and-markets/load-planning/LoadPlanning.types'
import type { EnergyTypeEnum } from '../enums/energyType.enum'
import type { FuturesPeriodCode, FuturesPeriodMode } from '../types/future.interface'
import type { MeterOverviewQuery } from './types/backend/meter-overview.api'

export const queryKeys = buildModuleCacheKey({
  meterOverview: {
    oneByScope: (params: MeterOverviewQuery | null) => [params],
  },
  uiPreferences: {
    oneByKey: (key: string) => [key],
  },
  timeSeries: {
    atlasSelectionOptionsByLocationIds: (locationIds: number[]) => [locationIds],
  },
  analytics: {
    baselines: {
      oneById: (id?: string | null | number) => [id],
      manyByParams: (params: PaginatedQuery & { locationId?: number; companyId?: number }) => [params],
    },
    regressions: {
      oneById: (id?: string | null | number) => [id],
      manyByParams: (params: newPaginatedQuery & { locationId?: number; companyId?: number }) => [params],
    },
    visualizations: {
      oneById: (id?: string | null | number) => [id],
      manyByParams: (params: PaginatedQuery & { locationId?: number; companyId?: number }) => [params],
    },
    comparisons: {
      oneById: (id?: string | null | number) => [id],
      manyByParams: (params: PaginatedQuery & { locationId?: number; companyId?: number }) => [params],
    },
    customTimeSeries: {
      oneById: (id?: string | null | number) => [id],
      manyByParams: (params: PaginatedQuery & { locationPgId?: number; companyId?: number; regionId?: number; search?: string }) => [
        params,
      ],
    },
    timeSeriesData: {
      manyByParms: (params: { seriesIds: string[]; locationIds: number[]; from: string; to: string; granularity: string }) => [params],
    },
  },
  iso: {
    companyBaselineConfig: {
      fetch: (companyId?: number) => [companyId],
    },
    risks: {
      manyByCompanyId: (companyId?: number) => [companyId],
    },
    chapters: {
      all: () => [],
    },
    enpis: {
      manyByCompanyId: (companyId?: number) => [companyId],
      deviationAnalyses: {
        manyById: (enpiId?: number) => [enpiId],
      },
    },
    documentTemplates: {
      all: () => [],
    },
    documents: {
      oneById: (id?: number) => [id],
      manyByOptionalIds: (params: { locationId: any; chapterId: any; companyId: any; measureId: any }) => [params],
    },
    goals: {
      manyByCompanyId: (companyId?: number) => [companyId],
    },
    measures: {
      oneById: (id?: number) => [id],
      manyByCompanyId: (companyId?: number, locationId?: number) => [companyId, locationId],
      manyBacklogByCompanyId: (companyId?: number, locationId?: number) => [companyId, locationId],
      positionsById: (id?: number, valeriId?: number) => [id, valeriId],
    },
    insightInbox: {
      oneById: (id?: number) => [id],
      list: (params?: any) => [params],
      dismissalReasonConfig: () => [],
    },
    checklist: {
      latest: () => [],
    },
    simulations: {
      oneById: (id: number, from?: string, to?: string) => [id, from, to].filter(i => !!i),
      manyByCompanyId: (params: { companyId?: number }) => [params],
      manyByParams: (params: {
        companyId?: number
        page?: number
        limit?: number
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
      }) => [params],
      mainMeters: {
        manyByCompanyId: (companyId?: number) => [companyId],
      },
    },
    valeri: {
      companyAndLocationId: (companyId: number, locationId?: number) => [companyId, locationId],
      positionsById: (valeriId?: number) => [valeriId],
      defaultsByCompanyId: (companyId?: number) => [companyId],
      oneById: (id?: number) => [id],
      manyByCompanyId: (companyId?: number, locationId?: number) => [companyId, locationId],
    },
  },
  insightsOnboarding: {
    state: () => [],
  },
  insightSettings: {
    oneByMeterId: (meterId?: number) => [meterId],
    recommendationByMeterId: (meterId?: number) => [meterId],
    manyByLocationIds: (locationIds: number[]) => [locationIds],
    recommendationsByLocationIds: (locationIds: number[]) => [locationIds],
  },
  meters: {
    manyByParams: (params: MeterControllerGetMetersData) => [params],
    oneByDeviceAndBuildingId: (params: { deviceId?: string; buildingId?: number }) => [params],
    infraSettingsByLocationIds: (locationIds: number[]) => [locationIds],
    possibleParentMeters: (params: { deviceId?: string; buildingId?: number }) => [params],
    infos: (params: MeterControllerGetMetersInfoByAllowedLocationsData) => [params],
    deviceIdInfosBatched: (params: MeterControllerGetMetersInfoByAllowedLocationsData) => [params],
    sankey: (buildingId?: number, locationId?: number, from?: string, to?: string, lang?: string, calculationMode?: string) => [
      { buildingId, locationId, from, to, lang, calculationMode },
    ],
    sankeyCsv: (buildingId?: number, locationId?: number, from?: string, to?: string, lang?: string) => [
      { buildingId, locationId, from, to, lang },
    ],
    documents: {
      manyByMeterId: (meterId: number) => [meterId],
    },
    series: {
      byMeterAndBuilding: (params: { meterId?: number; buildingId?: number }) => [params],
    },
    energyEvaluation: {
      manyByParams: (params: MeterControllerGetEnergyEvaluationData) => [params],
    },
  },
  meterViews: {
    manyByCompanyId: (companyId: number) => [companyId],
    oneById: (id: number, from?: string, to?: string) => [id, from, to].filter(i => !!i),
    sankey: (id: number, from?: string, to?: string, lang?: string) => [id, from, to, lang].filter(i => !!i),
    sankeyCsv: (id: number, from?: string, to?: string, lang?: string) => [id, from, to, lang].filter(i => !!i),
  },
  locationProductionSchedules: {
    allForCaller: () => [],
  },
  load: {
    aggregatedMonthlyBaseloads: {
      byParams: (params: { locationId?: number; buildingId?: number; years?: number[] }) => [params],
    },
    aggregatedMonthlyPeakloads: {
      byParams: (params: { locationId?: number; buildingId?: number; years?: number[] }) => [params],
    },
  },
  procurement: {
    oneById: (id?: number) => [id],
    manyByLocationId: (locationId?: number) => [locationId],
    manyByCompanyId: (companyId?: string) => [companyId],
    previewForecast: {
      byParams: ({
        companyId,
        year,
        energyType,
        locationId,
        granularity,
        requestBody,
        id,
      }: {
        companyId?: number
        year?: number
        energyType?: string
        locationId?: number
        requestBody?: any
        granularity: GranularityEnum
        id?: string
      }) => [companyId, locationId, year, energyType, granularity, requestBody, id],
    },
    futures: {
      manyByDateRange: (params: { startDate: string; endDate: string; energyType: EnergyTypeEnum; periodMode: FuturesPeriodMode }) => [
        params,
      ],
      byPeriodAndDateRange: (params: {
        startDate: string
        endDate: string
        energyType: EnergyTypeEnum
        periodMode: FuturesPeriodMode
        year: number
        periodCode: FuturesPeriodCode
      }) => [params],
    },
    market: {
      one: () => [],
      products: {
        manyByParams: (params: { locationPgId: any; year?: number; energyType?: EnergyTypeEnum }) => [params],
      },
      fetchMarketTracksForAllProducts: (strategyId: string, startDate: string, endDate: string) => [strategyId, startDate, endDate],
    },
    invoiceAudits: {
      oneById: (invoiceId?: number) => [invoiceId],
      manyByCompanyId: (companyId?: string, page?: number) => [companyId, page],
      manyByLocationId: (locationId?: number, page?: number) => [locationId, page],
      manyByBuildingId: (buildingId?: number, page?: number) => [buildingId, page],
    },
    procurementInvoices: {
      oneById: (invoiceId?: number) => [invoiceId],
      manyByCompanyId: (companyId?: string, page?: number) => [companyId, page],
      manyByLocationId: (locationId?: number, page?: number) => [locationId, page],
      manyByBuildingId: (buildingId?: number, page?: number) => [buildingId, page],
    },
    prices: {
      oneByYear: (year?: number, energyType?: EnergyTypeEnum) => [year, energyType],
      manyByParams: (params: { companiesWithMainDevices: { deviceId?: string; source?: string; companyId?: string }[] }) => [params],
    },
    costSavings: {
      get: (strategyId?: number | string) => [strategyId],
    },
    config: {
      fetchCompanyConfig: (companyId: number) => [companyId],
    },
    staticStrategies: {
      fetchAll: () => [],
      fetchOne: (strategyId?: string) => [strategyId],
    },
    hedgedStrategies: {
      fetchAll: () => [],
      fetchOne: (strategyId?: string) => [strategyId],
    },
    forecast: {
      fetchOne: (year: number, energyType: ProcurementEnergyType, locationId?: number, companyId?: number) => [
        year,
        energyType,
        locationId,
        companyId,
      ],
    },
    strategies: {
      fetchOne: (id?: string, year?: string | null, energyType?: string | null) => [id, year, energyType],
      fetchStrategyOverviews: (companyId: string) => [companyId],
    },
    loadPlanning: {
      fetchAllForCompany: (
        energyType: ProcurementEnergyType,
        year?: number,
        companyId?: number,
        locationId?: number,
        granularity?: GranularityEnum
      ) => [year, energyType, companyId, locationId, granularity],
      fetchEarliestConsumption: (companyId: number, energyType: ProcurementEnergyType, year?: number) => [companyId, energyType, year],
    },
    plannedStrategies: {
      fetchForCompany: (companyId?: number) => [companyId],
      fetchOne: (strategyId?: string) => (strategyId ? [strategyId] : []),
    },
    loadAdjustments: {
      fetchAll: (year: number, energyType: ProcurementEnergyType, locationId?: number) => [year, energyType, locationId],
      fetchOne: (adjustmentId: string) => [adjustmentId],
    },
    loadAdjustmentsV2: {
      fetchAll: (energyType: ProcurementEnergyType, year?: number, companyId?: number, locationId?: number) => [
        energyType,
        year,
        companyId,
        locationId,
      ],
    },
    measureSimulation: {
      fetchCandidates: (companyId?: number) => [companyId],
      fetchByMeasureId: (measureId?: string) => [measureId],
    },
    loadPlanningsV2: {
      fetchAll: (companyId?: number) => [companyId],
      fetchOne: (companyId?: number, year?: number, energyType?: string) => [companyId, year, energyType],
    },
    portfolio: {
      overview: (strategyId: string) => [strategyId],
      loadProfile: (strategyId: string) => [strategyId],
    },
    portfolioTimeseries: {
      fetchByStrategy: (strategyId?: string, granularity?: string) => [strategyId, granularity],
    },
  },
  isCostEnabled: (companyId?: string) => [companyId],
  users: {
    byId: (userId?: number) => [userId],
    manyByCompanyId: (companyFirebaseId?: string, companyPgId?: number, fetchAllCompanyUsers = false) => [
      companyFirebaseId,
      companyPgId,
      fetchAllCompanyUsers,
    ],
    managementPage: (params: {
      companyId?: number
      page: number
      pageSize: number
      search?: string
      sortBy: string
      sortDirection: string
      invitedOnly: boolean
    }) => [params],
    fetch: (params: { email?: string; firebaseId?: string }) => [params],
  },
  invitations: {
    manyByCompanyId: (companyId?: number) => [companyId],
  },
  profilePicture: {},
  userAvatars: {
    manyByCompanyId: (companyId?: string) => [companyId],
  },
  firebaseCompany: {
    oneById: (firebaseCompanyId?: string) => [firebaseCompanyId],
  },
  locationsStats: {
    manyByParams: (params: {
      buildingId: any
      regionId: any
      filters: any
      locationId: any
      page: any
      limit: any
      orderBy: any
      orderDirection: any
      companyId: any
      year: any
      energyType?: 'electricity' | 'gas'
    }) => [params],
  },
  metrics: {
    manyByParams: (params: {
      year: number
      energyType?: 'electricity' | 'gas'
      companyId?: number
      regionId?: number
      locationId?: number
      buildingId?: number
      page: number
      limit: number
      orderBy?: string
      orderDirection?: 'asc' | 'desc'
    }) => [params],
  },
  consumptionTrends: {
    monthlyByParams: (params: { years: string[]; energyTypes: string[] }) => [params],
  },
  energyData: {
    manyByParams: (params: { source?: string; deviceId?: string; dateRange?: [Date, Date] }) => [params],
    aggregated: (params: {
      from?: string
      to?: string
      buildingId?: number
      regionId?: number
      locationFirebaseId?: string
      companyFirebaseId?: string
      resolution?: number
    }) => [params],
  },
  spotMarket: {
    manyByDateRange: (params: { startDate?: string; endDate?: string; companyId?: number; energyType?: EnergyTypeEnum }) => [params],
  },
  budget: {
    manyByParams: (params: { buildingId?: number; locationId?: number; companyId?: number }) => [params],
    manyByParamsV4: (params: { buildingId?: number; locationId?: number; companyId?: number }) => [params],
  },
  budgetTracking: {
    fetchByParams: (params?: BudgetTrackingControllerGetCostsData) => [params],
  },
  consumption: {
    upload: () => [],
    manyByParams: (params: { devices: string[]; from: string; to: string }) => [params],
    loadProfile: (params: { deviceIds: string[]; from?: string; to?: string }) => [params],
    series: (params: { deviceIds?: string[]; from?: string; to?: string; granularity?: string }) => [params],
  },
  submeterAttribution: {
    byMeterAndWindows: (params: { meterId?: number; window: string[]; includeSeries?: boolean }) => [params],
  },
  ember: {
    thread: {
      oneByUserId: (userId?: string) => [userId],
    },
  },
  modules: (userId?: number) => [userId],
  news: {
    latest: () => [],
  },
  company: {
    logo: (params: { companyId?: number; logoId?: number | null }) => [params],
    whiteLabelLogo: (params: { companyId?: number; whiteLabelLogoId?: number | null }) => [params],
    regions: (params: { companyId: number }) => [params],
    userRegions: (params: { companyId: number; userId: number }) => [params],
    premiumFeatures: (companyId?: number) => [companyId],
  },
  location: {
    getLocationAndBuildings: (companyId?: number) => [companyId],
  },
  city: {
    fetchAll: (page?: number) => [page],
  },
  reports: {
    oneById: (reportId?: number) => [reportId],
    manyByCompanyId: (companyId?: number) => [companyId],
    logo: (params: { reportId?: number; logoId?: number | null }) => [params],
    modules: {
      manyByReportId: (reportId?: number) => [reportId],
      oneById: (reportId?: number, moduleId?: number, limit?: number) => [reportId, moduleId, limit].filter(i => i !== undefined),
      image: (params: { reportId?: number; moduleId?: number }) => [params],
    },
  },
  sentReports: {
    oneByParams: (params: { reportId?: number; sentReportId?: number }) => [params],
    logo: (params: { reportId?: number; sentReportId?: number; logoId?: number | null }) => [params],
    modules: {
      manyByParams: (params: { reportId?: number; sentReportId?: number }) => [params],
      image: (params: {
        reportId?: number
        sentReportId?: number
        moduleId?: number
        pdfToken?: string | null
        imageId?: string | null
      }) => [params],
    },
  },
  sso: {
    strategies: {
      byCompanySlug: (companySlug: string) => [companySlug],
    },
  },
  machineCategories: {
    all: () => [],
  },
  gaps: {
    getByBuilding: (buildingId?: number) => [buildingId],
    getAllByCompany: (companyId?: number) => [companyId],
  },
  dataCompleteness: {
    getDataCompleteness: (request: DataCompletenessControllerGetDataCompletenessData) => [request],
  },
  shortTermForecast: {
    byParams: (params: {
      from?: string
      to?: string
      granularity?: number
      dataType?: string
      companyId?: number
      locationId?: number
      buildingId?: number
      deviceId?: string
    }) => [params],
  },
})

// helper types
type PaginatedQuery = { filters?; page: number; limit: number; sortBy: string | undefined; sortOrder: 'asc' | 'desc' | undefined }

type newPaginatedQuery = { filters?; page: number; pageSize: number; sortBy: string | undefined; sortOrder: 'asc' | 'desc' | undefined }

// Query key factory
// ------------------------------------------------------------------------------------------------------------------
type Cast<X, Y> = X extends Y ? X : Y

type Fn = (...args: any[]) => any[]

type KeyConfig = {
  [k: string]: KeyConfig | Fn | unknown
}

type CacheKeyHelperType<T extends KeyConfig, P extends string[] = []> = {
  [k in keyof T]: T[k] extends (...args: any[]) => any[]
    ? {
        toKey: () => [...P, k]
        toKeyWithArgs: (...args: Parameters<T[k]>) => [...P, k, ...ReturnType<T[k]>]
      }
    : T[k] extends KeyConfig
      ? { toKey: () => [...P, k] } & CacheKeyHelperType<Cast<T[k], KeyConfig>, Cast<[...P, k], string[]>>
      : { toKey: () => [...P, k] }
}

function buildModuleCacheKey<T extends KeyConfig>(keyConfig: T, prefix: string[] = []): CacheKeyHelperType<T> {
  const keyFn = (name: string) => prefix.concat([name])
  const toolObj = {} as KeyConfig
  for (const k of Object.keys(keyConfig)) {
    const v = keyConfig[k]
    if (typeof v === 'function') {
      toolObj[k] = {
        toKeyWithArgs: (...args: unknown[]) => [...keyFn(k), ...v(...args)],
        toKey: () => keyFn(k),
      }
    } else if (v instanceof Object) {
      toolObj[k] = { toKey: () => keyFn(k), ...buildModuleCacheKey(v as KeyConfig, keyFn(k)) }
    } else {
      toolObj[k] = {
        toKey: () => keyFn(k),
      }
    }
  }
  return toolObj as CacheKeyHelperType<T>
}
